<script lang="ts">
	/**
	 * @fileoverview BatchDeleteDevicesModal - Modal for bulk device deletion
	 * This component provides an interface for bulk deletion of selected devices. 
	 * It supports different types of devices: * - Single devices (no channels) 
	 * - Multi-channel devices (channels 0-99) * - Devices with channels over 100 
	 * - Shared and non-shared devices * * The component automatically groups devices by
	main ID and uses the correct * API endpoints for each device type.
 	*/
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';
	//Stores
	import { resetEditUpdateStore, update } from 'src/stores/ui/edit_lists';
	import { devices } from 'src/stores/lists/devices';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { load_image_from } from 'src/helpers/interface_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	//Design System Components
	import { Button, Container, ListItem, Modal, Text, toast } from '@Shelly/shelly-design-system';

	export let showModal: boolean;

	// State
	let isLoading = false;

	// Constants
	const CHANNEL_THRESHOLD = 100;
	const CHANNEL_SUFFIX_REGEX = /\d+$/;

	// Helper functions
	/**
	 * Extracts the main device ID from the full device ID
	 * @param deviceId - The full device ID
	 * @returns The main device ID (before '_')
	 */
	const getMainDeviceId = (deviceId: string): string => deviceId.split('_')[0];

	/**
	 * Checks if a device ID has a channel number suffix
	 * @param deviceId - The device ID to check
	 * @returns true if has channel suffix, false otherwise
	 */
	const hasChannelSuffix = (deviceId: string): boolean =>
		deviceId.includes('_') && CHANNEL_SUFFIX_REGEX.test(deviceId);

	/**
	 * Checks if the device is multi-channel (channels 0-99)
	 * @param device - The device to check
	 * @returns true if multi-channel device, false otherwise
	 */
	const isMultiChannelDevice = (device: Device): boolean => {
		// Multi-channel devices have channels_count > 1 and channel < 100
		// Channel 0 (main) doesn't have suffix, channels 1+ have suffix
		return device.channels_count > 1 && device.channel < CHANNEL_THRESHOLD;
	};

	/**
	 * Checks if the device has a channel over 100
	 * @param device - The device to check
	 * @returns true if channel is >= 100, false otherwise
	 */
	const isChannelOver100 = (device: Device): boolean =>
		hasChannelSuffix(device.id) && device.channel >= CHANNEL_THRESHOLD;

	/**
	 * Generates all channel IDs for a multi-channel device
	 * @param device - The device to generate channel IDs for
	 * @returns Array of all channel IDs for the device
	 */
	const generateChannelIds = (device: Device): string[] => {
		if (!isMultiChannelDevice(device)) return [device.id];

		const mainId = getMainDeviceId(device.id);
		return Array.from({ length: device.channels_count }, (_, i) =>
			i === 0 ? mainId : `${mainId}_${i}`,
		);
	};

	/**
	 * Groups devices by main ID
	 * @param devices - Array of devices to group
	 * @returns Map with main IDs as keys and arrays of devices as values
	 */
	const groupDevicesByMainId = (devices: Device[]): Map<string, Device[]> => {
		const groups = new Map<string, Device[]>();

		devices.forEach((device) => {
			// For multi-channel devices (channel 0-99), group by main ID
			// For single channel or channel >= 100, group by full ID
			const groupKey =
				device.channels_count > 1 && device.channel < CHANNEL_THRESHOLD
					? getMainDeviceId(device.id)
					: device.id;

			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			groups.get(groupKey)!.push(device);
		});

		return groups;
	};

	// The logic for determining the API endpoint is as follows:
	// Channel 1-99 + shared: /device/bulk_delete_shared
	// Channel 1-99 + non-shared: /device/bulk_delete
	// Channel >= 100 + shared: /device/bulk_delete_shared
	// Channel >= 100 + non-shared: /interface/device/bulk_delete
	// No channel + shared: /device/bulk_delete_shared
	// No channel + non-shared: /device/bulk_delete

	/**
	 * Separates devices into shared and non-shared based on their type and determines the correct endpoints
	 * @param deviceGroups - Grouped devices by main ID
	 * @returns Object with arrays of IDs for different device types and for display
	 */
	const separateSharedAndNonShared = (deviceGroups: Map<string, Device[]>) => {
		const sharedIds: string[] = [];
		const nonSharedIds: string[] = [];
		const channelOver100Ids: string[] = [];
		const displayIds: string[] = [];

		deviceGroups.forEach((devices, groupKey) => {
			const firstDevice = devices[0];
			const isMultiChannel =
				firstDevice.channels_count > 1 && firstDevice.channel < CHANNEL_THRESHOLD;
			const isOver100 = isChannelOver100(firstDevice);

			if (isOver100) {
				// Channel >= 100 devices
				displayIds.push(firstDevice.id);

				if (firstDevice.shared) {
					// Shared devices with channel >= 100 use shared endpoint
					sharedIds.push(firstDevice.id);
				} else {
					// Non-shared devices with channel >= 100 use special endpoint
					channelOver100Ids.push(firstDevice.id);
				}
			} else if (isMultiChannel) {
				if (firstDevice.shared) {
					// For shared multichannel devices: show only selected channels
					displayIds.push(...devices.map((d) => d.id));
					sharedIds.push(...devices.map((d) => d.id));
				} else {
					// For non-shared multichannel devices: show all channels
					displayIds.push(...generateChannelIds(firstDevice));
					nonSharedIds.push(groupKey); // Use main ID
				}
			} else {
				// Single channel device (no multichannel)
				displayIds.push(firstDevice.id);

				const targetArray = firstDevice.shared ? sharedIds : nonSharedIds;
				targetArray.push(firstDevice.id);
			}
		});

		return { sharedIds, nonSharedIds, channelOver100Ids, displayIds };
	};

	/**
	 * Gets the device image URL
	 * @param device - The device to get the image for
	 * @returns Image URL or undefined if no image
	 */
	const getDeviceImage = (device: Device): string | undefined => {
		if (!device?.image) return undefined;

		const serverUrl = device?.localImage ? '' : device?.server;
		return load_image_from(device.image, serverUrl).replace('thumb_', '');
	};

	// API calls
	/**
	 * Deletes non-shared devices via the standard endpoint
	 * @param ids - Array of device IDs to delete
	 * @returns Promise with the API request result
	 */
	const deleteNonSharedDevices = (ids: string[]) =>
		postReq('/device/bulk_delete', { ids: JSON.stringify(ids) });

	/**
	 * Deletes shared devices via the specialized endpoint
	 * @param ids - Array of device IDs to delete
	 * @returns Promise with the API request result
	 */
	const deleteSharedDevices = (ids: string[]) =>
		postReq('/device/bulk_delete_shared', { ids: JSON.stringify(ids) });

	/**
	 * Deletes devices with channels over 100 via the interface endpoint
	 * @param ids - Array of device IDs to delete
	 * @returns Promise with the API request result
	 */
	const deleteChannelOver100Devices = (ids: string[]) =>
		postReq('/interface/device/bulk_delete', { ids: JSON.stringify(ids) });

	// UI helpers
	/**
	 * Shows success message for successful deletion
	 */
	const showSuccessMessage = () => {
		toast.open({
			type: 'success',
			title: $format('text_successful_deleted_devices', {
				default: 'Successfully deleted devices',
			}),
		});
	};

	/**
	 * Shows error message for deletion failure
	 * @param error - The error that occurred
	 */
	const showErrorMessage = (error: any) => {
		console.error('Error deleting devices:', error);
		toast.open({
			type: 'danger',
			title: $format('text_failed_delete_devices', {
				default: 'Failed to delete devices',
			}),
		});
	};

	/**
	 * Cleans up state and closes the modal
	 */
	const cleanup = () => {
		isLoading = false;
		resetEditUpdateStore();
		showModal = false;
	};

	// Reactive statements
	$: selectedDevices = Object.values($update.selectedDevices as { [key: string]: Device }) as Device[];

	$: deviceGroups = groupDevicesByMainId(selectedDevices);
	$: ({ sharedIds, nonSharedIds, channelOver100Ids, displayIds } =
		separateSharedAndNonShared(deviceGroups));
	$: devicesToShow = displayIds.map((id) => $devices[id]).filter(Boolean);
	$: modalTitle = capFirstChar($format(`sub_settings_title_${$update.type}`));

	// Event handlers
	/**
	 * Handles form submission for device deletion
	 * Sends requests to different endpoints based on device types
	 * @returns Promise that resolves when the operation completes
	 */
	const submit = async (): Promise<void> => {
		if (selectedDevices.length === 0) return;

		isLoading = true;

		try {
			const promises = [
				nonSharedIds.length > 0 && deleteNonSharedDevices(nonSharedIds),
				sharedIds.length > 0 && deleteSharedDevices(sharedIds),
				channelOver100Ids.length > 0 && deleteChannelOver100Devices(channelOver100Ids),
			].filter(Boolean);

			const results = await Promise.all(promises);

			if (results.every((response) => response.isok)) {
				showSuccessMessage();
			} else {
				throw new Error('Some deletions failed');
			}
		} catch (error) {
			showErrorMessage(error);
		} finally {
			cleanup();
		}
	};

	/**
	 * Handles modal close event
	 */
	const onClose = (): void => {
		showModal = false;
	};
</script>

<Modal on:close={onClose} {showModal} title={modalTitle}>
	<Container slot="content" padding="0">
		<Text element="h2" class="mb-m">
			{$format('text_modal_batch_delete_devices', {
				default: 'Are you sure you want to delete the selected devices?',
			})}
		</Text>

		{#each devicesToShow as device}
			<ListItem
				listItemBgColor="--color-1"
				class="mb-s"
				title={device.name}
				image={getDeviceImage(device)}
				icon={device.icon}
				viewType="fullColored"
				on:click={() => {}}
			/>
		{/each}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={onClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			loading={isLoading}
			disabled={selectedDevices.length === 0}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			on:click={submit}
		>
			{$format('button_delete', { default: 'Delete' })}
		</Button>
	</div>
</Modal>
